import React, { useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Lightbox for photos & videos with keyboard / button navigation.
 * items: [{ type: 'image'|'video', src: string, alt?: string }]
 */
const MediaLightbox = ({ items = [], index = 0, open, onClose, onIndexChange }) => {
  const touchStartX = useRef(null);
  const videoRef = useRef(null);

  const total = items.length;
  const current = total > 0 ? items[((index % total) + total) % total] : null;

  const go = useCallback((delta) => {
    if (!total) return;
    const next = ((index + delta) % total + total) % total;
    onIndexChange?.(next);
  }, [index, total, onIndexChange]);

  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, go]);

  useEffect(() => {
    // pause video when switching away
    if (videoRef.current && current?.type !== 'video') {
      try { videoRef.current.pause(); } catch (_) {}
    }
  }, [index, current?.type]);

  if (!open || !current) return null;

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 50) return;
    go(dx > 0 ? -1 : 1);
  };

  return (
    <div
      className="media-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Media viewer"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="media-lightbox__top" onClick={(e) => e.stopPropagation()}>
        <span className="media-lightbox__counter">
          {index + 1} / {total}
        </span>
        <button type="button" className="media-lightbox__icon-btn" onClick={onClose} aria-label="Close">
          <X size={22} />
        </button>
      </div>

      {total > 1 && (
        <button
          type="button"
          className="media-lightbox__nav media-lightbox__nav--prev"
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          aria-label="Previous"
        >
          <ChevronLeft size={28} />
        </button>
      )}

      <div
        className="media-lightbox__stage"
        onClick={(e) => e.stopPropagation()}
      >
        {current.type === 'video' ? (
          <video
            key={current.src}
            ref={videoRef}
            src={current.src}
            controls
            autoPlay
            playsInline
            className="media-lightbox__media"
          />
        ) : (
          <img
            key={current.src}
            src={current.src}
            alt={current.alt || ''}
            className="media-lightbox__media"
          />
        )}
      </div>

      {total > 1 && (
        <button
          type="button"
          className="media-lightbox__nav media-lightbox__nav--next"
          onClick={(e) => { e.stopPropagation(); go(1); }}
          aria-label="Next"
        >
          <ChevronRight size={28} />
        </button>
      )}

      {total > 1 && (
        <div className="media-lightbox__thumbs" onClick={(e) => e.stopPropagation()}>
          {items.map((item, i) => (
            <button
              key={`${item.src}-${i}`}
              type="button"
              className={`media-lightbox__thumb${i === index ? ' is-active' : ''}`}
              onClick={() => onIndexChange?.(i)}
              aria-label={`Media ${i + 1}`}
            >
              {item.type === 'video' ? (
                <span className="media-lightbox__thumb-video">▶</span>
              ) : (
                <img src={item.src} alt="" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLightbox;
